import React from 'react';

export const ALERT_PAGE_SIZE = 10;
export const ALERT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;
const SEARCH_DELAY_MS = 300;

interface PagedFilters {
    page?: number;
    pageSize?: number;
    search?: string;
}

interface PagedResult {
    pagination: { totalPages: number };
}

function errorMessage(error: unknown) {
    return error instanceof Error ? error.message : 'Không thể tải cảnh báo.';
}

// A filtered, server-paged alert list: changing a filter goes back to page 1, typing in the search waits a moment,
// and an older answer arriving late never replaces a newer one.
export function usePagedAlerts<F extends PagedFilters, R extends PagedResult>(
    fetchPage: (filters: F) => Promise<R>,
    refreshToken: number,
) {
    const [filters, setFilters] = React.useState<F>({ page: 1, pageSize: ALERT_PAGE_SIZE } as F);
    const [searchInput, setSearchInput] = React.useState('');
    const [data, setData] = React.useState<R | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const requestIdRef = React.useRef(0);
    const fetchRef = React.useRef(fetchPage);
    fetchRef.current = fetchPage;

    React.useEffect(() => {
        const search = searchInput.trim() || undefined;
        if (search === filters.search) return undefined;
        const timer = window.setTimeout(() => {
            setFilters(current => ({ ...current, search, page: 1 }));
        }, SEARCH_DELAY_MS);
        return () => window.clearTimeout(timer);
    }, [searchInput, filters.search]);

    const load = React.useCallback(async () => {
        const requestId = ++requestIdRef.current;
        setLoading(true);
        setError('');
        try {
            const next = await fetchRef.current(filters);
            if (requestId !== requestIdRef.current) return;
            const lastPage = Math.max(1, next.pagination.totalPages);
            if ((filters.page ?? 1) > lastPage) {
                setFilters(current => ({ ...current, page: lastPage }));
                return;
            }
            setData(next);
        } catch (loadError) {
            if (requestId === requestIdRef.current) setError(errorMessage(loadError));
        } finally {
            if (requestId === requestIdRef.current) setLoading(false);
        }
    }, [filters]);

    React.useEffect(() => {
        void load();
    }, [load, refreshToken]);

    const setFilter = React.useCallback(<K extends keyof F>(key: K, value: F[K] | '' | undefined) => {
        setFilters(current => ({ ...current, [key]: value === '' ? undefined : value, page: 1 }));
    }, []);

    const patchFilters = React.useCallback((patch: Partial<F>) => {
        setFilters(current => ({ ...current, ...patch, page: 1 }));
    }, []);

    const setPage = React.useCallback((page: number) => {
        setFilters(current => ({ ...current, page }));
    }, []);

    return { data, loading, error, filters, setFilter, patchFilters, setPage, searchInput, setSearchInput, reload: load };
}
